import { Button } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import enLocale from 'date-fns/locale/en-US';
import huLocale from 'date-fns/locale/hu';
import { useSelector } from 'react-redux';

const localeMap = { en: enLocale, hu: huLocale };

const normLang = (lang) => {
	const s = String(lang || '').trim();
	if (!s) return 'en';
	if (localeMap[s]) return s;
	const short = s.split('-')[0];
	return localeMap[short] ? short : 'en';
};

const normSeverity = (s) => String(s || '').toUpperCase();
const isReadValue = (v) => v === true || v === 1 || v === 'true';

const severityButtonClass = (severity, read) => {
	if (read) return 'bg-gray-200 text-gray-800';
	switch (severity) {
		case 'SUCCESS': return 'bg-green-800 hover:bg-green-900 text-white';
		case 'ERROR': return 'bg-red-800 hover:bg-red-900 text-white';
		case 'WARNING': return 'bg-orange-800 hover:bg-orange-900 text-white';
		case 'INFO': return 'bg-gray-800 hover:bg-gray-900 text-white';
		default: return 'bg-black text-white hover:text-black';
	}
};

const getBackgroundCalculateLink = (bg, notificationId) => {
	if (!bg || bg.id == null) return null;
	const type = normSeverity(bg.type);
	if (type === 'WHATIF' || type === 'OPTIMIZE') return '/what-if/edit/' + bg.id + '/N' + notificationId;
	return '/background-calculate/' + (type || 'UNKNOWN') + '/' + bg.id + '/N' + notificationId;
};

function NotificationCard(props) {
	const currentLanguageId = useSelector(({ i18n }) => i18n.language);

	const { item, className } = props;
	const severity = normSeverity(item?.severity);
	const isRead = isReadValue(item?.read);
	const localeId = normLang(currentLanguageId);
	const createdDate = item?.created ? new Date(item.created) : new Date();

	const handleClose = () => { if (props.onClose) props.onClose(item.id); };
	const handleRead = () => { if (props.onReadToggle) props.onReadToggle(item.id, !isRead); };

	function ItemIcon() {
		switch (severity) {
			case 'ERROR': return (<Icon className='mr-8 opacity-75' color='inherit'>cancel</Icon>);
			case 'SUCCESS': return (<Icon className='mr-8 opacity-75' color='inherit'>check_circle</Icon>);
			case 'WARNING': return (<Icon className='mr-8 opacity-75' color='inherit'>error_outline</Icon>);
			case 'INFO': return (<Icon className='mr-8 opacity-75' color='inherit'>info</Icon>);
			default: return null;
		}
	}

	const cardClassName = clsx(
		'flex items-center relative w-full rounded-12 p-16 min-h-64 shadow',
		isRead ? 'bg-gray-100 text-black opacity-90' : '',
		!isRead && severity === 'SUCCESS' && 'bg-green-600 text-white',
		!isRead && severity === 'ERROR' && 'bg-red-600 text-white',
		!isRead && severity === 'WARNING' && 'bg-orange-600 text-white',
		!isRead && severity === 'INFO' && '',
		className
	);

	return (
		<Card className={cardClassName} elevation={0}>
			<div className='w-full'>
				<div className='flex items-center'>
					{/* <ItemIcon /> */}
					<Typography className='pr-12' component='div'>{item?.message}</Typography>
					<IconButton disableRipple className='top-0 right-0 absolute p-12' color='inherit' size='small' onClick={handleClose}>
						<Icon className='text-12 opacity-75' color='inherit'>close</Icon>
					</IconButton>
				</div>

				<div>{props.children}</div>

				<div className='py-4 text-xs italic'>
					{formatDistanceToNow(createdDate, { addSuffix: true, locale: localeMap[localeId] || enLocale })}
				</div>

				<div className='w-full h-22 flex gap-8 mt-6'>
					<Button className={'flex-1 text-xs ' + severityButtonClass(severity, isRead)} onClick={handleRead}>
						{isRead ? 'Mark as Unread' : 'Mark as Read'}
					</Button>
					<Button className={'flex-1 text-xs ' + severityButtonClass(severity, isRead)} onClick={handleClose}>
						Delete
					</Button>
				</div>


				{!!item?.data?.meta?.backgroundCalculate?.id && (<BackgroundCalculateButtons className='mt-4' item={item} severity={severity} />)}
			</div>
		</Card>
	);
}

function BackgroundCalculateButtons(props) {
	const bg = props?.item?.data?.meta?.backgroundCalculate;
	const to = getBackgroundCalculateLink(bg, props?.item?.id);
	if (!to) return null;

	return (
		<div className={'flex flex-col mt-6 ' + (props.className || '')}>
			<Button className={'text-xs mt-4 ' + severityButtonClass(props.severity, false)} component={Link} to={to}>
				View
			</Button>
		</div>
	);
}

export default NotificationCard;
